
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const useRSVPUpdatesAllowed = (eventId: string) => {
  const navigate = useNavigate();
  const [updatesAllowed, setUpdatesAllowed] = useState<boolean | null>(null);
  const [checkingUpdatesAllowed, setCheckingUpdatesAllowed] = useState(true);

  // Check if RSVP updates are allowed
  useEffect(() => {
    if (eventId) {
      const checkRSVPUpdatesAllowed = async () => {
        try {
          setCheckingUpdatesAllowed(true);
          
          // Get event status
          const { data: event, error: eventError } = await supabase
            .from('events')
            .select('status')
            .eq('event_id', eventId)
            .single();
            
          if (eventError) throw eventError;
          
          // If event is not in "planned" status, RSVP updates are not allowed
          if (event.status !== 'planned') {
            setUpdatesAllowed(false);
            return;
          }
          
          // Check if there are any date recommendations
          const { data: dateRecs, error: dateError } = await supabase
            .from('event_date_recommendations')
            .select('recommendation_id')
            .eq('event_id', eventId)
            .limit(1);
            
          if (dateError) throw dateError;
          
          // Check if there are any venue recommendations
          const { data: venueRecs, error: venueError } = await supabase
            .from('event_venue_recommendations')
            .select('recommendation_id')
            .eq('event_id', eventId)
            .limit(1);
            
          if (venueError) throw venueError;
          
          // If there are no recommendations, RSVP updates are allowed
          const allowed = (!dateRecs || dateRecs.length === 0) && 
                          (!venueRecs || venueRecs.length === 0);
                          
          setUpdatesAllowed(allowed);
        } catch (error) {
          console.error("Error checking if RSVP updates are allowed:", error);
          // Default to not allowed if there's an error
          setUpdatesAllowed(false);
        } finally {
          setCheckingUpdatesAllowed(false);
        }
      };
      
      checkRSVPUpdatesAllowed();
    }
  }, [eventId]);
  
  // Redirect if updates are not allowed
  useEffect(() => {
    if (updatesAllowed === false && !checkingUpdatesAllowed) {
      toast("RSVP updates not available", {
        description: "The host is currently finalizing plans for this event. RSVP updates are no longer available.",
        position: "top-center"
      });
      navigate(`/events/${eventId}`);
    }
  }, [updatesAllowed, checkingUpdatesAllowed, navigate, eventId]);

  return {
    updatesAllowed,
    checkingUpdatesAllowed
  };
};
